import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import searchIcon from "../../../assets/img/search-icon.svg";
import upSolidIcon from "../../../assets/img/header-chevron-up-solid.svg";
import downSolidIcon from "../../../assets/img/header-chevron-down-solid.svg";
import { ExportButton } from "../../../components/export.component";
import { InvoiceFilter } from "../../../features/invoice/invoiceFilter.component";
import { InvoiceList } from "../../../features/invoice/invoiceList.component";
import { ActionInvoiceFilter, useAppSelector } from "../../../core/redux";
import { useAppDispatch } from "../../../core/redux/ReduxStore";
import {
  ActionsInvoiceTable,
  TypeActionsInvoiceTable,
} from "../../../core/redux/slices/sliceInvoiceTable";
import { PagedRequestParam } from "../../../models/api";
import { useTablePaginationSearchFilter } from "../../../hooks";
import { ApiSchemaFilteredInvoice } from "../../../models/api/invoice";
import { AppRouteApi, AppRouteUi } from "../../../config/appRoute";
import { SearchQueryParams } from "../../../config/constants";
import { useHistory } from "react-router-dom";

export const PageInvoiceList = () => {
  const timeoutId = useRef<number>();
  const history = useHistory();
  const searchParam = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const fromDate = useMemo(
    () => searchParam.get(SearchQueryParams.FromDate),
    [searchParam]
  );
  const toDate = useMemo(
    () => searchParam.get(SearchQueryParams.ToDate),
    [searchParam]
  );
  const filterMissingInvoice = fromDate || toDate ? true : false;
  const {
    pageNumber,
    pageSize,
    StartDate,
    EndDate,
    searchKey,
    nextPageToken,
    shouldRefetch,
    FilterByPatient,
    InsuranceTypeId,
  } = useAppSelector((state) => ({
    ...state.invoiceTable,
    ...state.InvoiceFilter,
  }));
  const apiParameters = useMemo(() => {
    return {
      startDate: StartDate || fromDate,
      endDate: EndDate || toDate,
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: searchKey,
      NextPageToken: nextPageToken,
      PatientId: FilterByPatient,
      InsuranceTypeId: InsuranceTypeId,
    };
  }, [
    EndDate,
    StartDate,
    pageSize,
    pageNumber,
    searchKey,
    nextPageToken,
    fromDate,
    toDate,
    FilterByPatient,
    InsuranceTypeId,
  ]);
  const {
    apiData: invoiceData,
    isFetching: isInvoiceDataFetching,
    refetch,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredInvoice,
    TypeActionsInvoiceTable
  >(ActionsInvoiceTable, {
    apiRoute: AppRouteApi.Invoice.V2(filterMissingInvoice),
    customParams: apiParameters,
  });
  const [tempSearchKey, setTempSearchKey] = useState(searchKey);
  const [pagedRequestParam, setPagedRequestParam] = useState<PagedRequestParam>(
    {}
  );
  const dispatch = useAppDispatch();

  const searchPatientList = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(ActionsInvoiceTable.goToPage(1));
      setTempSearchKey(ev.target.value);
      setPagedRequestParam({ searchKey: ev.target.value });
      clearTimeout(timeoutId.current);
      timeoutId.current = setTimeout(
        () => dispatch(ActionsInvoiceTable.setSearchKey(ev.target.value)),
        500
      ) as unknown as number;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
    }
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    history.replace(AppRouteUi.Invoice.List());
  }, [history]);

  useEffect(() => {
    return () => {
      dispatch(ActionsInvoiceTable.resetData());
      dispatch(ActionInvoiceFilter.resetData());
    };
  }, [dispatch]);

  return (
    <div className="row gx-0">
      <div className="col-12 mb-3">
        <div className="content-box bg-white h-100">
          <div className="content-box-header">
            <div className="row align-items-center  justify-content-between">
              <div className="col-md-8 col-12 content-box-title">
                <div>
                  <p className="text-capitalize my-1 text-green-200">Invoice</p>
                </div>
                <div className="collapse-icon"></div>
              </div>
              <div className="col-md-4 col-12 mt-1">
                <form
                  action=""
                  className="search-component d-flex justify-content-end"
                >
                  <div className="input-group me-3">
                    <span
                      className="input-group-text pe-2"
                      id="search-component-word"
                    >
                      <img src={searchIcon} alt="search-icon" />
                    </span>
                    <input
                      type="text"
                      className="form-control ps-0"
                      placeholder="Search..."
                      aria-label="Search"
                      aria-describedby="search-component-word"
                      onChange={searchPatientList}
                      value={tempSearchKey}
                      onKeyPress={(ev) => {
                        ev.key === "Enter" && ev.preventDefault();
                      }}
                    />
                    {tempSearchKey.length > 0 && (
                      <span
                        className="input-group-text text-green-200"
                        id="search-component-word"
                      >
                        <i
                          className="fas fa-times click"
                          onClick={() => {
                            setTempSearchKey("");
                            dispatch(ActionsInvoiceTable.setIsLoading(true));
                            clearTimeout(timeoutId.current);
                            dispatch(ActionsInvoiceTable.setSearchKey(""));
                            dispatch(ActionsInvoiceTable.setIsLoading(false));
                          }}
                        />
                      </span>
                    )}
                  </div>
                  <ExportButton
                    params={pagedRequestParam}
                    component="Appointment"
                  />
                  <a
                    className="btn box-collapse-btn me-1"
                    data-bs-toggle="collapse"
                    href="#dashboard-collapsable-AllAppointment"
                    role="button"
                    aria-expanded="false"
                    aria-controls="dashboard-collapsable-AllAppointment"
                  >
                    <img
                      src={upSolidIcon}
                      alt="up"
                      className="img-fluid svg-btn collapse-close"
                    />
                    <img
                      src={downSolidIcon}
                      alt="down"
                      className="img-fluid svg-btn collapse-open"
                    />
                  </a>
                </form>
              </div>
            </div>
          </div>
          <div
            className="collapse show clearfix position-relative"
            id="dashboard-collapsable-AllAppointment"
          >
            <div className="border-top"></div>
            <div
              className="mt-1 mb-1 px-2 border-bottom border-1"
              id="filterPanel"
            >
              <InvoiceFilter />
            </div>
            <div className="content-box-body">
              <InvoiceList
                totalCount={invoiceData?.totalCount || 0}
                data={invoiceData?.list}
                isValidating={isInvoiceDataFetching}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
